import { ModalScreen } from "./modal-screen";
import { customElement, state } from "lit/decorators.js";
import { html } from "lit";
import { AsepriteResource } from "@excaliburjs/plugin-aseprite";
import "./floating-select-menu";
import { Color } from "excalibur";
import FigureThumbnail from "@/utils/figure-thumbnail";
import AssetLoader from "@/utils/asset-loader";
import {
  parseSpriteDescription,
  getClothingByPart,
  updateSpriteDescription,
} from "@/utils/character";
import gameApp from "@/index";
import { CustomToast } from "./toast";

@customElement("dressing-room")
export class DressingRoom extends ModalScreen {
  assetLoader: any;
  thumbnailAvatar: any;
  currentHair: string;

  private defaultAction = "down";
  private defaultAnimation = "idle_down";

  private items: any = [];
  private colors: any = [];
  private skinTones: string[] = [];
  figureThumbnail: FigureThumbnail;
  currentLook: string;

  constructor() {
    super();
    this.tabs = [
      { slug: "hair", label: "Hair" },
      { slug: "top", label: "Top" },
      { slug: "bottom", label: "Bottom" },
    ];

    this.assetLoader = new AssetLoader();
  }

  async loadData() {
    const response = await fetch(
      `${process.env.BASE_API_URL}/dressing-room?category=${this.activeTab}`,
      {
        method: "GET",
        credentials: "include",
      }
    );

    const data = await response.json();

    this.items = data.items;
    this.colors = data.colors;
    this.skinTones = data.skinTones;

    this.requestUpdate();
  }

  async openModal() {
    this.isOpen = true;
    this.figureThumbnail = new FigureThumbnail();

    // Retrieve and parse user data from localStorage
    const userData = localStorage.getItem("tam_user");
    this.currentLook = userData ? JSON.parse(userData).look : null;

    await this.selectTab("hair");

    const { skinColor } = parseSpriteDescription(this.currentLook);

    const skinColorRGB = Color.fromHex(skinColor);
    skinColorRGB.a = 0.5;

    const hair = getClothingByPart(this.currentLook, "hr");
    const hairColorRGB = Color.fromHex(hair.color);
    hairColorRGB.a = 0.5;

    const legs = getClothingByPart(this.currentLook, "lg");
    const legsColorRGB = Color.fromHex(legs.color);
    legsColorRGB.a = 0.5;

    const torso = getClothingByPart(this.currentLook, "ts");
    const torsoColorRGB = Color.fromHex(torso.color);
    torsoColorRGB.a = 0.5;

    this.thumbnailAvatar = await this.figureThumbnail.createMultipleThumbnails({
      head: {
        x: 17,
        y: 0,
        animationName: this.defaultAction,
        color: skinColorRGB.toString(),
      },
      [hair.type]: {
        x: 17,
        y: 0,
        animationName: this.defaultAction,
        color: hairColorRGB.toString(),
      },
      body: {
        x: 0,
        y: -67,
        animationName: this.defaultAnimation,
        color: skinColorRGB.toString(),
      },
      [legs.type]: {
        x: 0,
        y: -67,
        animationName: this.defaultAnimation,
        color: legsColorRGB.toString(),
      },
      [torso.type]: {
        x: 0,
        y: -67,
        animationName: this.defaultAnimation,
        color: torsoColorRGB.toString(),
      },
    });

    this.requestUpdate();
  }

  public async selectTab(tabId: string) {
    this.activeTab = tabId;

    // Load necessary data asynchronously
    await this.loadData();
    await this.loadAssets();
  }

  private async loadAssets() {
    try {
      // Load all assets
      await this.assetLoader.loadMultipleAsepriteResources(this.items);
      for (const items of this.items) {
        const asset = this.assetLoader.getAssetById(items.slug);
        await this.setThumbnailImage(items.slug, asset.resource);
      }
    } catch (error) {
      console.error("Error loading assets:", error);
    }
  }

  private async setThumbnailImage(slug: string, resource: AsepriteResource) {
    if (!resource) return;

    const sprite = resource?.getSpriteSheet()?.sprites?.[0]; // Safe access using optional chaining
    if (sprite?.image?.data && sprite.sourceView) {
      const item = this.items.find((item) => item.slug === slug);
      if (item) {
        try {
          const { skinColor } = parseSpriteDescription(this.currentLook);

          const skinColorRGB = Color.fromHex(skinColor);
          skinColorRGB.a = 0.5;

          const hair = getClothingByPart(this.currentLook, "hr");
          const hairColorRGB = Color.fromHex(hair.color);
          hairColorRGB.a = 0.5;

          const torso = getClothingByPart(this.currentLook, "ts");
          const torsoColorRGB = Color.fromHex(torso.color);
          torsoColorRGB.a = 0.7;

          const legs = getClothingByPart(this.currentLook, "lg");
          const legsColorRGB = Color.fromHex(legs.color);
          legsColorRGB.a = 0.7;


          let thumbnail;
          if (this.activeTab === "top") {
            thumbnail = await this.figureThumbnail.createMultipleThumbnails(
              {
                head: {
                  x: 0,
                  y: 0,
                  animationName: this.defaultAction,
                  color: skinColorRGB.toString(),
                  anchor: { x: 0.5, y: 0 },
                },
                [hair.type]: {
                  x: 17,
                  y: 0,
                  animationName: this.defaultAction,
                  color: hairColorRGB.toString(),
                },
                body: {
                  x: 0,
                  y: -67,
                  animationName: this.defaultAnimation,
                  color: skinColorRGB.toString(),
                },
                [item.slug]: {
                  x: 0,
                  y: -67,
                  animationName: item.animation,
                  color: torsoColorRGB.toString(),
                  anchor: { x: 0.5, y: 0 },
                },
              },
              false
            );
          } else if (this.activeTab === "bottom") {
            thumbnail = await this.figureThumbnail.createMultipleThumbnails(
              {
                head: {
                  x: 0,
                  y: 0,
                  animationName: this.defaultAction,
                  color: skinColorRGB.toString(),
                  anchor: { x: 0.5, y: 0 },
                },
                [hair.type]: {
                  x: 17,
                  y: 0,
                  animationName: this.defaultAction,
                  color: hairColorRGB.toString(),
                },
                body: {
                  x: 0,
                  y: -67,
                  animationName: this.defaultAnimation,
                  color: skinColorRGB.toString(),
                },
                [item.slug]: {
                  x: 0,
                  y: -67,
                  animationName: item.animation,
                  color: legsColorRGB.toString(),
                  anchor: { x: 0.5, y: 0 },
                },
                [torso.type]: {
                  x: 0,
                  y: -67,
                  animationName: this.defaultAnimation,
                  color: torsoColorRGB.toString(),
                },
              },
              false
            );
          } else {
            thumbnail = await this.figureThumbnail.createMultipleThumbnails(
              {
                head: {
                  x: 0,
                  y: 0,
                  animationName: this.defaultAction,
                  color: skinColorRGB.toString(),
                  anchor: { x: 0.5, y: 0 },
                },
                [item.slug]: {
                  x: 0,
                  y: 0,
                  animationName: item.animation,
                  color: hairColorRGB.toString(),
                  anchor: { x: 0.5, y: 0 },
                },
                body: {
                  x: 0,
                  y: -67,
                  animationName: this.defaultAnimation,
                  color: skinColorRGB.toString(),
                },
                [torso.type]: {
                  x: 0,
                  y: -67,
                  animationName: this.defaultAnimation,
                  color: torsoColorRGB.toString(),
                },
              },
              false
            );
          }

          if (thumbnail) {
            item.image = thumbnail; // Uncomment if you plan to assign the thumbnail
            this.requestUpdate();
          } else {
            console.error("Thumbnail creation failed.");
          }
        } catch (error) {
          console.error("Error creating thumbnail:", error);
        }
      } else {
        console.error(`Hair type with slug "${slug}" not found.`);
      }
    } else {
      console.error("Invalid sprite data or source view.");
    }
  }

  private async selectItem(slug: string) {
    const partList = {
      hair: "hr",
      top: "ts",
      bottom: "lg",
    };

    const part = partList[this.activeTab];

    const currentItem = getClothingByPart(this.currentLook, part);
    if (!currentItem) return console.error("Current item not found.");

    console.log(currentItem.color);
    const colorRGB = Color.fromHex(currentItem.color);
    colorRGB.a = 0.5;

    const item = this.items.find((item) => item.slug === slug);
    if (!item) return;

    const color = item.image?.querySelector(`#${slug} div`)?.style
      .backgroundColor;
    if (!color) return console.error(`Element "${slug}" not found.`);
    // Change part of outfit
    await this.figureThumbnail.replaceThumbnailSprite(
      this.thumbnailAvatar,
      currentItem.type,
      slug,
      item.animation
    );

    // Update color of the sprite
    this.figureThumbnail.updateThumbnailPart(
      this.thumbnailAvatar,
      slug,
      "color",
      color
    );

    // Save current look temporarly
    const updates = [
      {
        partToUpdate: part,
        newType: slug,
        newColor: Color.fromRGBString(color).toHex().replace("#", ""),
      },
    ];

    const newLook = updateSpriteDescription(this.currentLook, updates);
    this.currentLook = newLook;

    this.requestUpdate();
  }

  private selectSkinColor(color: string) {
    const skinColorRGB = Color.fromHex(color);
    skinColorRGB.a = 0.5;

    this.figureThumbnail.updateThumbnailPart(
      this.thumbnailAvatar,
      "head",
      "color",
      skinColorRGB
    );

    this.figureThumbnail.updateThumbnailPart(
      this.thumbnailAvatar,
      "body",
      "color",
      skinColorRGB
    );

    for (const item of this.items) {
      this.figureThumbnail.updateThumbnailPart(
        item.image,
        "head",
        "color",
        skinColorRGB
      );

      this.figureThumbnail.updateThumbnailPart(
        item.image,
        "skin",
        "color",
        skinColorRGB
      );
    }

    const updates = [
      {
        partToUpdate: "sk",
        newType: skinColorRGB.toHex().replace("#", ""),
      },
    ];

    const newLook = updateSpriteDescription(this.currentLook, updates);
    this.currentLook = newLook;

    this.requestUpdate();
  }

  // New method to handle color selection
  private selectColor(color: string) {
    this.requestUpdate();

    const colorRGB = Color.fromHex(color);
    colorRGB.a = 0.5;

    for (const item of this.items) {
      this.figureThumbnail.updateThumbnailPart(
        item.image,
        item.slug,
        "color",
        colorRGB.toString()
      );
    }
  }

  public getRandomValue(currentValue: string) {
    const hairRandom = ["short", "mohawk"];
    const availableStyles = hairRandom.filter(
      (style) => style !== currentValue
    );

    const randomIndex = Math.floor(Math.random() * availableStyles.length);
    return availableStyles[randomIndex];
  }

  private onSave() {
    const toast = document.querySelector("custom-toast") as CustomToast;
    toast?.openToast("Saved! You're now legally stylish 😎✨", "top-right", 3000, "success");
    gameApp.wsClient.sendMessage(
      JSON.stringify({
        action: "outfit_actions",
        arguments: {
          newLook: this.currentLook,
        },
      })
    );
  }

  async onRandom() {
    const currentHair = getClothingByPart(this.currentLook, "hr").type;
    this.currentHair = this.getRandomValue(this.currentHair || currentHair);

    const look = `sk(4287f5)-bd(male)-ts(tanktop:ffffff)-hr(${this.currentHair}:4287f5)-lg(pants:0000FF)`;
    const { skinColor } = parseSpriteDescription(look);

    const skinColorRGB = Color.fromHex(skinColor);
    skinColorRGB.a = 0.5;

    const hair = getClothingByPart(look, "hr");
    const hairColorRGB = Color.fromHex(hair.color);
    hairColorRGB.a = 0.7;

    const legs = getClothingByPart(look, "lg");
    const legsColorRGB = Color.fromHex(legs.color);
    legsColorRGB.a = 0.7;

    const torso = getClothingByPart(look, "ts");
    const torsoColorRGB = Color.fromHex(torso.color);
    torsoColorRGB.a = 0.7;

    this.thumbnailAvatar = await this.figureThumbnail.createMultipleThumbnails({
      head: {
        x: 17,
        y: 0,
        animationName: this.defaultAction,
        color: skinColorRGB.toString(),
      },
      [hair.type]: {
        x: 17,
        y: 0,
        animationName: this.defaultAction,
        color: hairColorRGB.toString(),
      },
      body: {
        x: 0,
        y: -67,
        animationName: this.defaultAnimation,
        color: skinColorRGB.toString(),
      },
      [legs.type]: {
        x: 0,
        y: -67,
        animationName: this.defaultAnimation,
        color: legsColorRGB.toString(),
      },
      [torso.type]: {
        x: 0,
        y: -67,
        animationName: this.defaultAnimation,
        color: torsoColorRGB.toString(),
      },
    });

    this.requestUpdate();
  }

  protected createRenderRoot() {
    return this;
  }

  render() {
    if (!this.isOpen) return html``;

    return html`
      <div class="modal">
        <div class="modal__top">
          ${this.tabs.map(
            (tab) => html`
              <span
                class="modal__tab ${this.activeTab === tab.slug
                  ? "is-active"
                  : ""}"
                @click="${() => this.selectTab(tab.slug)}"
              >
                ${tab.label}
              </span>
            `
          )}
          <button class="modal__close" @click="${this.closeModal}">
            <span>Close</span>
          </button>
        </div>

        <div class="modal__body">
          <div
            style="display: flex; justify-content: space-between; align-items: start;"
          >
            <div class="cards has-3">
              ${this.items.map(
                (item) => html`
                  <div class="card">
                    <h3>${item.title}</h3>
                    <div
                      class="card-image ${this.activeTab === "hair" ||
                      this.activeTab === "top"
                        ? "is-compact"
                        : ""}"
                    >
                      ${item.image}
                    </div>
                    <button
                      class="btn"
                      @click="${() => this.selectItem(item.slug)}"
                    >
                      Wear
                    </button>
                  </div>
                `
              )}
            </div>
            <div class="card">
              <div style="position: relative; margin-top: 0;">
                <div
                  style="display: flex; justify-content: center; width: 100%; height: 100px; margin-top: 20px; margin-bottom: 20px;"
                >
                  ${this.thumbnailAvatar}
                </div>
              </div>
              <div style="width: 100%;">
                <h3>Skin tone</h3>
                <div
                  style="display: flex; gap: 10px; padding-bottom: 15px; padding-top: 10px; width: 150px; flex-wrap: wrap;"
                >
                  ${this.skinTones.map(
                    (color) => html`
                      <div
                        style="width: 10px; height: 10px; cursor: pointer; padding: 5px; position: relative; z-index: 20;"
                        class="navigator_location"
                        @click="${() => this.selectSkinColor(color)}"
                      >
                        <span
                          style="background-color: ${color}; width: 10px; height: 10px;"
                        ></span>
                      </div>
                    `
                  )}
                </div>
                <button class="btn" @click="${() => this.onRandom()}">
                  Random
                </button>
                <button class="btn" @click="${() => this.onSave()}">
                  Save
                </button>
              </div>
            </div>
          </div>

          <div style="display: flex; gap: 10px; padding: 20px;">
            ${this.colors.map(
              (color) => html`
                <div
                  style="width: 10px; height: 10px; cursor: pointer; padding: 5px; position: relative; z-index: 20;"
                  class="navigator_location"
                  @click="${() => this.selectColor(color)}"
                >
                  <span
                    style="background-color: ${color}; width: 10px; height: 10px;"
                  ></span>
                </div>
              `
            )}
          </div>
        </div>
      </div>
    `;
  }
}
